import { z } from "zod";

export const QUOTE_STATUSES = ["new", "read", "responded", "archived"] as const;
export const QUOTE_BOOKING_STATUSES = [
  "pending",
  "confirmed",
  "rejected",
] as const;

export const QuotePhotoSchema = z.object({
  id: z.number().int(),
  url: z.string(),
  position: z.number().int(),
});

export type QuotePhoto = z.infer<typeof QuotePhotoSchema>;

export const QuoteTimePreferenceSchema = z.object({
  id: z.number().int(),
  preferredTime: z.string(),
  preferenceOrder: z.number().int(),
  display: z.string(),
});

export type QuoteTimePreference = z.infer<typeof QuoteTimePreferenceSchema>;

export const QuoteBookingSchema = z.object({
  id: z.number().int(),
  startTime: z.string(),
  endTime: z.string(),
  status: z.enum(QUOTE_BOOKING_STATUSES),
  confirmedAt: z.string().nullable(),
  display: z.string(),
});

export type QuoteBooking = z.infer<typeof QuoteBookingSchema>;

export const QuoteSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  postalCode: z.string().nullable(),
  service: z.string(),
  message: z.string(),
  status: z.enum(QUOTE_STATUSES),
  createdAt: z.string(),
  createdAtDisplay: z.string(),
  readAt: z.string().nullable(),
  timePreferences: z.array(QuoteTimePreferenceSchema).optional().default([]),
  booking: QuoteBookingSchema.nullable().optional(),
  photos: z.array(QuotePhotoSchema).optional().default([]),
});

export type Quote = z.infer<typeof QuoteSchema>;

export const UpcomingQuoteVisitSchema = z.object({
  id: z.number().int(),
  startTime: z.string(),
  endTime: z.string(),
  customerName: z.string(),
  customerEmail: z.string(),
  customerPhone: z.string().nullable(),
  service: z.string(),
  display: z.string(),
});

export type UpcomingQuoteVisit = z.infer<typeof UpcomingQuoteVisitSchema>;

export const WebsiteQuotesResponseSchema = z.object({
  quotes: z.array(QuoteSchema),
  unreadQuoteCount: z.number().int(),
  upcomingVisits: z.array(UpcomingQuoteVisitSchema),
});

export type WebsiteQuotesResponse = z.infer<typeof WebsiteQuotesResponseSchema>;

export function quotesSummaryLabel(
  unreadQuoteCount: number,
  quoteCount: number,
): string {
  if (quoteCount === 0) {
    return "No quotes yet. New requests from your site show up here.";
  }
  if (unreadQuoteCount > 0) {
    const unit = unreadQuoteCount === 1 ? "new quote" : "new quotes";
    return `${unreadQuoteCount} ${unit}`;
  }
  const unit = quoteCount === 1 ? "quote" : "quotes";
  return `${quoteCount} ${unit}`;
}
